import React from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge'
import { Link as RouterLink } from 'react-router-dom'
import useListenerCountStore from '../../store/listenerCount'
import useStationListenerCountSettingsStore from '../../store/stationListenerCounts'
import {
  formatListenerCountRelativeTime,
  resolveListenerCountStale,
} from '../../utils/listenerCount'
import { dashboardCardHeadingTypography, dashboardCardValueTypography } from './typography'
import useCountUp from './hooks/useCountUp'

export interface CurrentListenerCountCardProps {
  stationId: number | null
}

const CurrentListenerCountCard: React.FC<CurrentListenerCountCardProps> = ({ stationId }) => {
  const theme = useTheme()
  const listenerSettings = useStationListenerCountSettingsStore(
    React.useCallback(
      (state) => (stationId ? state.listenerCounts[stationId] : undefined),
      [stationId],
    ),
  )
  const { count, lastUpdated, stale, loading, hasFetched, error, nextRefreshAt } =
    useListenerCountStore((state) => ({
      count: state.count,
      lastUpdated: state.lastUpdated,
      stale: state.stale,
      loading: state.loading,
      hasFetched: state.hasFetched,
      error: state.error,
      nextRefreshAt: state.nextRefreshAt,
    }))
  const configLoaded = Boolean(listenerSettings?.loaded)
  const configEnabled = Boolean(listenerSettings?.enabled)
  const configEndpoint = listenerSettings?.endpoint ?? null

  const resolvedStale = resolveListenerCountStale(stale, lastUpdated)
  const isCountValid = React.useMemo(
    () => typeof count === 'number' && Number.isFinite(count) && count >= 0,
    [count],
  )
  const normalizedCount = React.useMemo(() => (isCountValid ? count : 0), [count, isCountValid])
  const animatedCount = useCountUp(normalizedCount, { initialValue: 0 })
  const integerFormatter = React.useMemo(
    () => new Intl.NumberFormat(undefined, { maximumFractionDigits: 0, minimumFractionDigits: 0 }),
    [],
  )
  const gaugeMax = React.useMemo(() => {
    const base = normalizedCount > 0 ? normalizedCount * 1.25 : 100
    return Math.max(100, Math.ceil(base / 10) * 10)
  }, [normalizedCount])
  const roundedAnimatedValue = React.useMemo(
    () => (isCountValid ? Math.round(animatedCount) : 0),
    [animatedCount, isCountValid],
  )
  const gaugeValue = isCountValid ? roundedAnimatedValue : 0
  const displayValue = isCountValid ? integerFormatter.format(roundedAnimatedValue) : '–'
  const lastUpdatedRelative = lastUpdated ? formatListenerCountRelativeTime(lastUpdated) : null
  const lastUpdatedAbsolute = lastUpdated ? new Date(lastUpdated) : null
  const nextRefreshRelative =
    nextRefreshAt && nextRefreshAt > Date.now()
      ? formatListenerCountRelativeTime(nextRefreshAt)
      : null
  const errorRelative = error ? formatListenerCountRelativeTime(error.occurredAt) : null

  const headingStyles = React.useMemo(() => dashboardCardHeadingTypography(theme), [theme])
  const valueStyles = React.useMemo(() => dashboardCardValueTypography(theme), [theme])

  let lastUpdatedLabel = ''
  let body: React.ReactNode

  if (!stationId) {
    lastUpdatedLabel = 'Select a station…'
    body = (
      <Typography color="text.secondary">
        Choose a station from the header to see the current listener count.
      </Typography>
    )
  } else if (!configLoaded) {
    lastUpdatedLabel = 'Loading settings…'
    body = (
      <Stack spacing={0.75} sx={{ mt: 0.75 }}>
        <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 3 }} />
        <Skeleton variant="text" width="70%" />
      </Stack>
    )
  } else if (!configEnabled || !configEndpoint) {
    lastUpdatedLabel = 'Listener counts are disabled…'
    body = (
      <Stack spacing={0.75} sx={{ mt: 0.75 }}>
        <Typography color="text.secondary">
          Listener counts are disabled for this station. Enable them from station settings to see
          live data.
        </Typography>
        <Button component={RouterLink} to="/settings/general" variant="outlined" size="small">
          Open settings
        </Button>
      </Stack>
    )
  } else {
    if (hasFetched) {
      const baseLabel = lastUpdatedRelative
        ? `Last updated ${lastUpdatedRelative}`
        : 'Last updated —'
      const absoluteLabel = lastUpdatedAbsolute
        ? ` (${lastUpdatedAbsolute.toLocaleString()})`
        : ''
      lastUpdatedLabel = `${baseLabel}${absoluteLabel}`
    } else {
      lastUpdatedLabel = 'Waiting for the first update…'
    }
    body = (
      <Stack spacing={0.75} sx={{ flexGrow: 1 }}>
        {error ? (
          <Alert severity="error" variant="outlined">
            Latest update failed{errorRelative ? ` ${errorRelative}` : ''}. We’ll retry
            automatically.
          </Alert>
        ) : null}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 150,
          }}
        >
          {hasFetched ? (
            <Gauge
              width={150}
              height={150}
              value={gaugeValue}
              valueMax={gaugeMax}
              valueMin={0}
              startAngle={-110}
              endAngle={110}
              cornerRadius={12}
              sx={{
                [`& .${gaugeClasses.valueArc}`]: {
                  fill: resolvedStale ? theme.palette.warning.main : theme.palette.primary.main,
                },
                [`& .${gaugeClasses.referenceArc}`]: {
                  fill: theme.palette.action.selected,
                },
                [`& .${gaugeClasses.valueText}`]: valueStyles,
                [`& .${gaugeClasses.label}`]: {
                  fontSize: theme.typography.body2.fontSize,
                  fill: theme.palette.text.secondary,
                },
              }}
              valueText={displayValue}
              label="Listeners"
            />
          ) : (
            <Skeleton variant="circular" width={150} height={150} />
          )}
          {loading ? (
            <Box
              sx={{
                position: 'absolute',
                top: theme.spacing(0.75),
                right: theme.spacing(0.75),
                pointerEvents: 'none',
              }}
            >
              <CircularProgress size={24} thickness={5} />
            </Box>
          ) : null}
        </Box>
      </Stack>
    )
  }

  const tooltipLines = React.useMemo(() => {
    const lines: string[] = []
    if (lastUpdatedLabel.trim()) {
      lines.push(lastUpdatedLabel)
    }
    if (nextRefreshRelative?.trim()) {
      lines.push(`Next refresh ${nextRefreshRelative}`)
    }
    return lines
  }, [lastUpdatedLabel, nextRefreshRelative])

  const tooltipContent = React.useMemo(() => {
    if (tooltipLines.length === 0) {
      return ''
    }
    return (
      <Stack spacing={0.5} alignItems="flex-start">
        {tooltipLines.map((line, index) => (
          <Typography key={index} variant="body2">
            {line}
          </Typography>
        ))}
      </Stack>
    )
  }, [tooltipLines])

  const tooltipDisabled = tooltipLines.length === 0
  const headingTypography = React.useMemo(() => dashboardCardHeadingTypography(theme), [theme])
  const valueTypography = React.useMemo(() => dashboardCardValueTypography(theme), [theme])

  return (
    <Paper
      elevation={6}
      sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.25,
          textAlign: 'center',
        }}
      >
        {resolvedStale ? (
          <Chip
            label="Stale"
            color="warning"
            size="small"
            sx={{ position: 'absolute', top: -theme.spacing(0.25), right: -theme.spacing(0.25) }}
          />
        ) : null}
        <Tooltip
          title={tooltipContent}
          disableHoverListener={tooltipDisabled}
          disableFocusListener={tooltipDisabled}
          disableTouchListener={tooltipDisabled}
        >
          <Typography component="h2" sx={headingStyles}>
            Current listener count
          </Typography>
        </Tooltip>
      </Box>
      {body}
    </Paper>
  )
}

export default CurrentListenerCountCard
